import React from 'react';
import { BarChart, Trash2, Layers, Clock } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation,
  institutionTypes
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты для сравнения. После выполнения расчета нажмите кнопку "Сохранить расчет".
        </p>
      </div>
    );
  }

  // Функция получения названия типа учреждения
  const getInstitutionTypeName = (id) => {
    const institution = institutionTypes.find(type => type.id === id);
    return institution ? institution.name : id;
  };

  // Сортировка расчетов от новых к старым
  const sortedCalculations = [...savedCalculations].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'расчет' : savedCalculations.length < 5 ? 'расчета' : 'расчетов'}
        </span>
      </div>

      <div className="space-y-4">
        {sortedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Тип учреждения:</span>
                <span>
                  {getInstitutionTypeName(calc.formData.institutionType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Студентов:</span>
                <span>
                  {formatNumber(calc.formData.studentsCount, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Стоимость внедрения:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(calc.results.totalImplementationCost, 0, calc.results.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Срок окупаемости:</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatNumber(calc.results.paybackPeriod, 1)} лет
                </span>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {new Date(calc.date).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>

      {/* Если сохранено 2+ расчета, показываем сравнительный анализ */}
      {savedCalculations.length >= 2 && (
        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Сравнительный анализ
          </h4>
          
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 space-y-3">
            {/* Сравнение по стоимости на студента */}
            <div>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Стоимость на студента
              </p>
              <div className="flex items-center space-x-1">
                {sortedCalculations.slice(0, 3).map((calc, index) => {
                  // Находим минимальную стоимость на студента среди всех расчетов
                  const minCostPerStudent = Math.min(...sortedCalculations.map(c => c.results.totalCostPerStudent));
                  // Определяем отношение текущей стоимости к минимальной
                  const ratio = calc.results.totalCostPerStudent / minCostPerStudent;
                  const width = 100 / ratio;
                  
                  return (
                    <div key={index} className="flex-grow">
                      <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                        <div 
                          className="h-2 bg-indigo-500 dark:bg-indigo-400 rounded-full" 
                          style={{ width: `${width}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="truncate max-w-[80px]">{getInstitutionTypeName(calc.formData.institutionType)}</span>
                        <span className="font-medium">{formatCurrency(calc.results.totalCostPerStudent, 0, calc.results.currency)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Сравнение по сроку окупаемости */}
            <div>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Срок окупаемости
              </p>
              <div className="flex items-center space-x-1">
                {sortedCalculations.slice(0, 3).map((calc, index) => {
                  // Находим минимальный срок окупаемости среди всех расчетов
                  const minPaybackPeriod = Math.min(...sortedCalculations.map(c => c.results.paybackPeriod));
                  // Определяем отношение текущего срока к минимальному
                  const ratio = calc.results.paybackPeriod / minPaybackPeriod;
                  const width = 100 / ratio;
                  
                  return (
                    <div key={index} className="flex-grow">
                      <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                        <div 
                          className="h-2 bg-emerald-500 dark:bg-emerald-400 rounded-full" 
                          style={{ width: `${width}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="truncate max-w-[80px]">{getInstitutionTypeName(calc.formData.institutionType)}</span>
                        <span className="font-medium">{formatNumber(calc.results.paybackPeriod, 1)} лет</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;